(ns core.front.i18n
  {:clj-kondo/config {:linters {:re-frame.reg-sub/unnamespaced-key {:level :off}}}}
  (:require [app.front.util :as futil]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.config :as front-config]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [taoensso.tempura :as tempura]))

;; -- Subscriptions -----------------------------------------------------------

(def default-tconfig
  {:default-locale i18n-model/default-lang
   :locales        {:en {:short-name "En"
                         :name       "English"}}
   :dict           {:en {:missing "*** Missing localization! ***"}}})

(defn language-query [db]
  (:language db i18n-model/default-lang))


(rf/reg-sub :language
  :-> language-query)


(rf/reg-sub :tconfig
  (fn [db _]
    (-> db
        (:tconfig default-tconfig))))

(rf/reg-sub ::locales
  :<- [:tconfig]
  (fn [tconfig _] (-> tconfig :locales)))

(rf/reg-sub ::full-locale
  :<- [:language]
  :<- [::front-config/country]
  (fn [[language country]]
    (let [str-lang (name language)]
      (if (or (str/includes? str-lang "-")
              (not country))
        str-lang
        (str str-lang "-" (str/upper-case country))))))

(defn- dict-loaded?
  "`default-tconfig` carries nothing but a `:missing` entry, so any locale with real
   content means the dictionary has been fetched and `::dict-loaded` has run."
  [opts]
  (boolean (some #(seq (dissoc % :missing)) (vals (:dict opts)))))

(defn missing-tr [{:keys [opts locales resource-ids]}]
  (if-not (dict-loaded? opts)
    ;; The dictionary is still in flight, so every key is "missing". Render nothing
    ;; instead of flashing the missing-localization marker, and keep the console quiet.
    ""
    (when-some [k (first resource-ids)]
      (js/console.error (str "Missing " (first locales) " i18n key " k)))))

(defn tempura-tr-fn [opts]
  (tempura/new-tr-fn (assoc opts :missing-resource-fn missing-tr)))

(defn tr-query [tconfig lang]
  (partial (tempura-tr-fn tconfig) [lang]))

(rf/reg-sub ::tempura-tr
  :<- [:tconfig]
  tempura-tr-fn)

(rf/reg-sub ::tempura-tr-quiet
  :<- [:tconfig]
  #(tempura/new-tr-fn (assoc % :missing-resource-fn (constantly ""))))

;; Returns new translations fn 'tr' after :language or :tconfig changed in app-db.
(rf/reg-sub :tr
  :<- [::tempura-tr]
  :<- [:language]
  (fn [[tempura-tr lang] _]
    (partial tempura-tr [lang])))

;; Like `:tr`, but returns nil for unknown keys
(rf/reg-sub :tr/quiet
  :<- [::tempura-tr-quiet]
  :<- [:language]
  (fn [[tempura-tr lang] _]
    (comp not-empty
          (partial tempura-tr [lang]))))

;; Compatibility localization function for string keys as used by Lupapiste
(rf/reg-sub :legacy-loc
  :<- [:tr]
  (fn [tr _]
    (fn [i18n-string-key]
      (if (and (string? i18n-string-key)
               (not (str/blank? i18n-string-key)))
        (->> (str/split i18n-string-key #"\." 2)
             (apply keyword)
             vector
             tr)
        (do (js/console.error (str "Invalid legacy-loc-key: " i18n-string-key))
            (-> default-tconfig :dict :en :missing))))))

;; -- Events ------------------------------------------------------------------


(rf/reg-cofx :tr
  (fn [{:keys [db] :as coeffects} _]
    (let [tconfig (:tconfig db)
          lang    (language-query db)
          tr      (tr-query tconfig lang)]
      (assoc coeffects :tr tr))))


(defn set-html-lang-attr! [language]
  (.setAttribute (.-documentElement js/document) "lang" (name language)))


(rf/reg-fx :set-html-lang-attr set-html-lang-attr!)


(rf/reg-event-fx ::dict-loaded
  (fn [{:keys [db]} [_ _ {:keys [dict lang locales]}]]
    (merge {:db (cond-> db
                  (map? dict) (assoc-in [:tconfig :dict] dict)
                  locales (assoc-in [:tconfig :locales] locales)
                  lang (assoc :language lang))}
           (when lang
             {:fx [[:set-html-lang-attr lang]]}))))


(rf/reg-event-fx ::change-language
  (fn [{:keys [db]} [_ language]]
    {:db                 (assoc db :language language)
     :comm/command       {:command :user/set-locale
                          :method  :post
                          :data    {:locale language}
                          :on-done [::dict-loaded]}
     :local-storage-set! ["selected-lang" (name language)]
     :fx                 [[:set-html-lang-attr language]]}))


(rf/reg-event-fx ::initialize.done
  (fn [_ [_ status body]]
    (js/console.error "::submit-failure" status body)
    nil))

(defn get-local-locale
  "Get selected locale from local storage. Normally you should
  subscribe :language, but if you need it during initialization
  events you need to use this. Note that it may be nil, in which
  case your backend must know how to parse the request language."
  []
  (futil/local-storage-get "selected-lang"))

(rf/reg-event-fx ::initialize
  (fn [_ _]
    {:comm/command (merge {:command    :i18n/dict
                           :method     :get
                           :on-success [::dict-loaded]
                           :on-done    [::initialize.done]}
                          (when-let [locale (get-local-locale)]
                            {:data (util/mmap locale)}))}))


(rf/reg-event-fx ::locale-dict-loaded
  (fn [{:keys [db]} [_ _ {:keys [dict locales]}]]
    {:db (cond-> db
           (map? dict) (update-in [:tconfig :dict] merge dict)
           locales (update-in [:tconfig :locales] merge locales))}))

(rf/reg-event-fx ::fetch-locale-dict
  (fn [_ [_ locale]]
    {:comm/command {:command    :i18n/locale-dict
                    :method     :get
                    :data       (util/mmap locale)
                    :on-success [::locale-dict-loaded]}}))

(rf/reg-event-fx ::fetch-locale-dict-if-not-loaded
  (fn [{:keys [db]} [_ locale]]
    (when (empty? (get-in db [:tconfig :dict locale]))
      {:dispatch [::fetch-locale-dict locale]})))

(defn group-locales-handler
  "Group locales subscription handler, takes one input signal which is the authority-group
  entity or map."
  [group _]
  (i18n-model/group-languages group))
